import {FormattedMessage, useIntl} from "react-intl";
import {Button, Divider, Row, Space, Spin} from "antd";
import React, {useCallback, useContext, useEffect, useState} from "react";
import '../../assets/css/Dentobook.css';
import {isMobileOnly} from 'react-device-detect';
import {EnvironmentOutlined, FileTextOutlined, MedicineBoxOutlined} from "@ant-design/icons";
import UserIcon from "../common/UserIcon";
import FormButtons from "../common/FormButtons";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {Organization} from "../../api-client";
import GeneralForm from "./GeneralForm";
import {messages as healthAreaMessages} from "../../i18n/health-area";
import {getCountry} from "../../i18n/countries";
import {healthRegionMessages} from "../../i18n/health-regions";
import {useLocation} from "react-router-dom";
import {getOrganizationApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {tokenService} from "../../services/tokenService";

interface GeneralProps {
    isEditMode: boolean;
    setIsEditMode: Function;
}

const General = ({ isEditMode, setIsEditMode }: GeneralProps) => {
    const intl = useIntl();
    const location = useLocation();
    const organizationApi = getOrganizationApi();
    const { principal } = useContext(DentobookApplicationContext);
    const [organization, setOrganization] = useState<Organization | undefined>(undefined);

    const fetchOrganization = useCallback(async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) return;
        try {
            const response = await organizationApi.fetchOrganizationById(orgId);
            setOrganization(response.data);
        } catch (error) {
            logger.error("Failed to load organization:", error);
        }
    }, [organizationApi]);

    useEffect(() => {
        fetchOrganization();
    }, [location]);

    if (!organization) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin tip={<FormattedMessage id="loading" />} />
            </div>
        );
    }

    if (isEditMode) {
        return <GeneralForm organization={organization} onClose={() => setIsEditMode(false)} />;
    }

    return (
        <>
            <MainInformation organization={organization} />
            <Divider />
            <ContactInformation organization={organization} />
            <Divider />
            <CompanyData organization={organization} getHealthAreaDisplayValue={key => getHealthAreaDisplayValue(key, intl)} getHealthRegionDisplayValue={key => getHealthRegionDisplayValue(key, intl)} />
            <FormButtons>
                <Button
                    onClick={() => setIsEditMode(true)}
                    className={"edit-btn " + (isMobileOnly ? "mobile-update-btn" : "dentobook-btn-primary")}
                    type="primary"
                    size="large"
                    shape="round"
                >
                    <FormattedMessage id="edit" />
                </Button>
            </FormButtons>
        </>
    );
}

const MainInformation = ({ organization }: { organization: Organization }) => {
    // Use presigned R2 URL from API response
    const avatarSrc = organization.avatarUrl;

    return (
        <>
            <h4><span className="text-blue mr-1"><MedicineBoxOutlined /></span> <FormattedMessage id="main_information" /></h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                {!avatarSrc ? (
                    <UserIcon size={isMobileOnly ? "medium" : "large"} />
                ) : (
                    <img
                        className="photo-img"
                        width={150}
                        src={avatarSrc}
                        alt="avatar"
                    />
                )}
                <Space direction="vertical" className={isMobileOnly ? "mb-2" : "ml-6 mb-72"}>
                    <span className="body-large-regular"><FormattedMessage id="name" />:</span>
                    <span className="body-large-medium">{organization.name}</span>
                </Space>
            </Space>
        </>
    );
};

const ContactInformation = ({ organization }: { organization: Organization }) => {
    const intl = useIntl();
    const countryData = getCountry(organization.country ?? '');
    const countryName = countryData ? (intl.locale === 'BG' ? countryData.name_bg : countryData.name_en) : (organization.country ?? undefined);

    return (
        <>
            <h4><span className="text-blue mr-1"><EnvironmentOutlined /></span> <FormattedMessage id="contact" /></h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                <ContactDetail labelId="email" value={organization.email ?? undefined} />
                <ContactDetail labelId="phone" value={organization.phone ?? undefined} />
                <ContactDetail labelId="website" value={organization.website ?? undefined} />
            </Space>
            <Row className="mt-2">
                <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                    <ContactDetail labelId="town" value={organization.town ?? undefined} />
                    <ContactDetail labelId="country" value={countryName} />
                    <ContactDetail labelId="address" value={organization.address ?? undefined} />
                </Space>
            </Row>
        </>
    );
};

const ContactDetail = ({ labelId, value }: { labelId: string, value?: string }) => (
    <Space direction="vertical" className={isMobileOnly ? "" : "ml-6"}>
        <span className="body-large-regular">{labelId ? <FormattedMessage id={labelId} /> : ''}:</span>
        <span className="body-large-medium">{value ?? <FormattedMessage id="not_available" />}</span>
    </Space>
);

const CompanyData = ({ organization, getHealthAreaDisplayValue, getHealthRegionDisplayValue }: { organization: Organization, getHealthAreaDisplayValue: (key: string) => string, getHealthRegionDisplayValue: (key: string) => string }) => {
    return (
        <>
            <h4><span className="text-blue mr-1"><FileTextOutlined /></span> <FormattedMessage id="company_data" /></h4>
            <Space className="mb-6" direction={isMobileOnly ? "vertical" : "horizontal"}>
                <CompanyDetail labelId="name" value={organization.companyName ?? undefined} />
                <CompanyDetail labelId="uic" value={organization.uic ?? undefined} />
                <CompanyDetail labelId="rzi_practice_code" value={organization.rziPracticeCode ?? undefined} />
                <CompanyDetail labelId="healthArea" value={organization.healthArea ? getHealthAreaDisplayValue(organization.healthArea) : undefined} />
            </Space>
            <Row className="mt-2 mb-8">
                <Space className="mb-6" direction={isMobileOnly ? "vertical" : "horizontal"}>
                    <CompanyDetail labelId="healthRegion" value={organization.healthRegion ? getHealthRegionDisplayValue(organization.healthRegion) : undefined} />
                    <CompanyDetail labelId="iban" value={organization.iban ?? undefined} />
                    <CompanyDetail labelId="accountable_person" value={organization.accountablePerson ?? undefined} />
                </Space>
            </Row>
        </>
    );
};

const CompanyDetail = ({ labelId, value }: { labelId: string, value?: string }) => (
    <Space direction="vertical" className={isMobileOnly ? "" : "ml-6"}>
        <span className="body-large-regular">{labelId ? <FormattedMessage id={labelId} /> : ''}:</span>
        <span className="body-large-medium">{value ?? <FormattedMessage id="not_available" />}</span>
    </Space>
);

const getDisplayValueForKey = (key: string, messageObject: any, type: string, intl: any) => {
    const messagesForLocale = messageObject[intl.locale] || {};
    const typeMessages = messagesForLocale[type] || {};
    return typeMessages[key] || key;
};

const getHealthAreaDisplayValue = (key: string, intl: any) => {
    return getDisplayValueForKey(key, healthAreaMessages, 'towns', intl);
};

const getHealthRegionDisplayValue = (key: string, intl: any) => {
    return getDisplayValueForKey(key, healthRegionMessages, 'healthRegions', intl);
};

export default General;